import { useEffect, useRef } from 'react';

/**
 * Shrinks font size uniformly across all `.text-wrapper span` cells in the row
 * so no cell overflows. All cells share the same size to keep the row consistent.
 */
function useAutoFitRow<T extends HTMLElement>(minFontSize = 9) {
  const ref = useRef<T>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const measure = () => {
      const cells = container.querySelectorAll<HTMLElement>('.text-wrapper');
      if (cells.length === 0) return;

      // Reset all spans before measuring — all writes before reads to avoid layout thrashing
      const items: { span: HTMLElement; cell: HTMLElement }[] = [];
      cells.forEach(cell => {
        const span = cell.querySelector<HTMLElement>('span');
        if (!span) return;
        span.style.fontSize = '';
        items.push({ span, cell });
      });

      if (items.length === 0) return;

      // Read from the span, not the container — container has no explicit font-size
      const defaultSize = parseFloat(getComputedStyle(items[0].span).fontSize);
      let minRequired = defaultSize;

      items.forEach(({ span, cell }) => {
        const spanWidth = span.getBoundingClientRect().width; // getBoundingClientRect, not scrollWidth — scrollWidth is 0 on inline elements
        const cellWidth = cell.clientWidth;
        if (cellWidth <= 0 || spanWidth <= 0) return;
        if (spanWidth > cellWidth) {
          const needed = Math.max((defaultSize * cellWidth) / spanWidth, minFontSize);
          if (needed < minRequired) minRequired = needed;
        }
      });

      if (minRequired < defaultSize) {
        items.forEach(({ span }) => {
          span.style.fontSize = `${minRequired}px`;
        });
      }
    };

    measure();

    // ResizeObserver re-runs measurement on layout changes (window resize etc.)
    // since those don't trigger a React re-render
    if (typeof ResizeObserver === 'undefined') {
      return; // Skip if ResizeObserver not available (SSR, older browsers, test env)
    }

    let rafId = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure); // rAF debounces rapid resize events
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [minFontSize]);

  return ref;
}

export default useAutoFitRow;
